/**
 * Bau-Bau Rytm — czysta logika (PLAN §14).
 * 3 tory, beatmapa 80–100 BPM, okno ±250 ms, pudło = plum bez kary.
 * Gwiazdki tylko rosną. Nagrody liczy serwer przez RunClient — tu tylko stats.
 */
import { MINIGAMES } from '@pieskowo/shared';

export const LANE_COUNT = 3 as const;
export type Lane = 0 | 1 | 2;

/** Margines trafienia (ms) — PLAN §14. */
export const HIT_WINDOW_MS = 250 as const;

export const BPM_MIN = 80 as const;
export const BPM_MAX = 100 as const;
export const BPM_DEFAULT = 90 as const;

/** Runda w zakresie 60–90 s (PLAN §14). */
export const ROUND_MS_MIN = 60_000 as const;
export const ROUND_MS_MAX = 90_000 as const;
export const ROUND_MS_DEFAULT = 75_000 as const;

/** Czas spadania nuty do strefy (ms) — wizualny lead-in. */
export const APPROACH_MS = 1_500 as const;

const RHYTHM = MINIGAMES.rhythm;
export const MAX_STARS = RHYTHM.maxStars;
export const GOOD_STARS_THRESHOLD = RHYTHM.goodStarsThreshold;
export const GAME_ID = RHYTHM.id;

export type RhythmNote = {
  id: string;
  lane: Lane;
  /** Moment idealnego trafienia od startu rundy (ms). */
  targetMs: number;
};

export type NoteResolution = 'pending' | 'hit' | 'miss';

export type Judgment = 'hit' | 'too_early' | 'miss' | 'none';

/** Deterministyczny LCG — powtarzalne beatmapy w testach. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type BeatmapOpts = {
  bpm?: number;
  durationMs?: number;
  seed?: number;
  /** Opóźnienie pierwszej nuty (ms). */
  leadInMs?: number;
  /** Cisza przed końcem rundy (ms). */
  tailMs?: number;
};

/**
 * Generuje nuty na 3 torach w rytm BPM.
 * Gęstość: co beat jedna nuta (losowy tor), bez kar za pudła.
 */
export function generateBeatmap(opts: BeatmapOpts = {}): RhythmNote[] {
  const bpm = clamp(
    opts.bpm ?? BPM_DEFAULT,
    BPM_MIN,
    BPM_MAX,
  );
  const durationMs = clamp(
    opts.durationMs ?? ROUND_MS_DEFAULT,
    ROUND_MS_MIN,
    ROUND_MS_MAX,
  );
  const leadInMs = opts.leadInMs ?? 2_000;
  const tailMs = opts.tailMs ?? 1_000;
  const rng = mulberry32(opts.seed ?? 42);
  const beatMs = 60_000 / bpm;
  const notes: RhythmNote[] = [];
  let t = leadInMs;
  let i = 0;
  let lastLane: Lane | null = null;

  while (t <= durationMs - tailMs) {
    let lane = Math.floor(rng() * LANE_COUNT) as Lane;
    // unikaj 3× z rzędu tego samego toru (czytelność dla 5–7 lat)
    if (lane === lastLane && rng() < 0.7) {
      lane = ((lane + 1 + Math.floor(rng() * 2)) % LANE_COUNT) as Lane;
    }
    notes.push({
      id: `n${i}`,
      lane,
      targetMs: Math.round(t),
    });
    lastLane = lane;
    i += 1;
    t += beatMs;
  }
  return notes;
}

export function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

/** Ocena pojedynczej nuty względem czasu tapnięcia. */
export function judgeNote(
  note: RhythmNote,
  tapMs: number,
  windowMs: number = HIT_WINDOW_MS,
): Judgment {
  const delta = tapMs - note.targetMs;
  if (Math.abs(delta) <= windowMs) return 'hit';
  if (delta < -windowMs) return 'too_early';
  return 'miss';
}

/**
 * Najbliższa nuta na torze w oknie trafienia (jeszcze nierozstrzygnięta).
 * Preferuje najmniejszy |delta|.
 */
export function findHittableNote(
  notes: readonly RhythmNote[],
  lane: Lane,
  nowMs: number,
  resolved: ReadonlySet<string>,
  windowMs: number = HIT_WINDOW_MS,
): RhythmNote | null {
  let best: RhythmNote | null = null;
  let bestAbs = Infinity;
  for (const n of notes) {
    if (n.lane !== lane) continue;
    if (resolved.has(n.id)) continue;
    const abs = Math.abs(nowMs - n.targetMs);
    if (abs > windowMs) continue;
    if (abs < bestAbs) {
      bestAbs = abs;
      best = n;
    }
  }
  return best;
}

/** Nuta minęła okno i nie została trafiona → plum (bez kary). */
export function isNoteExpired(
  note: RhythmNote,
  nowMs: number,
  windowMs: number = HIT_WINDOW_MS,
): boolean {
  return nowMs > note.targetMs + windowMs;
}

/** Gwiazdki tylko rosną, cap = maxStars z katalogu. */
export function addStar(current: number, maxStars: number = MAX_STARS): number {
  return Math.min(maxStars, Math.max(0, Math.floor(current)) + 1);
}

export function isGoodGame(stars: number): boolean {
  return stars >= GOOD_STARS_THRESHOLD;
}

/** Hub powrotu po Reward (PLAN §13.2 — entryPoint). */
export function returnSceneForEntry(entryPoint?: string): string {
  if (entryPoint === 'console') return 'ConsoleHub';
  // telefonik to overlay — wracamy do miasta (gracz otworzy telefonik z HUD)
  return 'Town';
}

/** Czas spawnu nuty na ekranie (lead-in spadania). */
export function spawnMsForNote(
  note: RhythmNote,
  approachMs: number = APPROACH_MS,
): number {
  return note.targetMs - approachMs;
}

/**
 * Score = liczba trafień × 100 (telemetria; serwer i tak liczy nagrodę z gwiazdek).
 * Nie używać do lokalnego liczenia zł.
 */
export function scoreFromHits(hits: number): number {
  return Math.max(0, Math.floor(hits)) * 100;
}
